import { ArrowRight, ChevronDown, Clock3, Database, Sparkles, type LucideIcon } from 'lucide-react';
import { useCallback, useState, type ReactNode } from 'react';
import { hashQuery, navigate, useHash } from '../../lib/router';
import './record.css';

/**
 * The record page (task 98, decision 0073): one layout for an insurer's client, a business owner's own
 * business and an admin's tenant. A soft header in the tenant's colour says whose record it is and what is
 * worth knowing first; folder tabs run from it into the page; the open tab shows a strip of figures, the
 * record on the left and its activity and next action on the right.
 *
 * The folder tabs are customer360's (PolicyHero.tsx `FolderTabs`, as on Policy 360 and Claims 360): the
 * same side shape, overlap, badge and shades, drawn from the tenant's colour.
 */

// —— the open tab, in the address ——

/** The `?tab=` of the hash, and a way to change it that replaces rather than adds a history entry. */
export function useRecordTab<T extends string>(tabs: readonly { id: T }[], param = 'tab') {
  const hash = useHash();
  const asked = hashQuery(hash).get(param);
  const active = (tabs.find((t) => t.id === asked) ?? tabs[0])!.id;
  const select = useCallback(
    (id: T) => {
      const [path] = window.location.hash.split('?');
      const q = hashQuery(window.location.hash);
      // The first tab is where the record opens, so it needs no parameter.
      if (id === tabs[0]?.id) q.delete(param);
      else q.set(param, id);
      const s = q.toString();
      // Moving between tabs of one record is not navigation: back leaves the record.
      navigate(`${path}${s ? `?${s}` : ''}`, { replace: true });
    },
    [tabs, param],
  );
  return { active, select };
}

// —— the frame ——

export function RecordFrame({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={`rec-frame${className ? ` ${className}` : ''}`}>{children}</div>;
}

export interface Crumb {
  label: string;
  href?: string;
}

/** The header: where the record sits, whose it is, its insights, and the tabs along its foot. */
export function RecordHero({
  crumbs,
  title,
  pill,
  subline,
  actions,
  aside,
  children,
  tabs,
  active,
  onSelect,
  tabsLabel,
}: {
  crumbs: Crumb[];
  title: ReactNode;
  pill?: ReactNode;
  /** Facts beside one another: a reference, a sector, a date. */
  subline?: ReactNode[];
  /** Top right: what can be done to the record. */
  actions?: ReactNode;
  /** Beside the title, under the actions: a score dial, say. */
  aside?: ReactNode;
  /** Between the title and the tabs: the insights. */
  children?: ReactNode;
  tabs: FolderTab[];
  active: string;
  onSelect: (id: string) => void;
  tabsLabel: string;
}) {
  return (
    <div className="rec-hero">
      <nav aria-label="Breadcrumb" className="rec-crumbs">
        {crumbs.map((c, i) => (
          <span key={i}>
            {i > 0 && <span aria-hidden="true">/</span>}
            {c.href ? <a href={c.href}>{c.label}</a> : <span aria-current="page">{c.label}</span>}
          </span>
        ))}
      </nav>
      <div className="rec-head">
        <div className="rec-head-main">
          <div className="rec-title">
            <h1>{title}</h1>
            {pill}
          </div>
          {subline && subline.length > 0 && (
            <p className="rec-subline">
              {subline.map((s, i) => (
                <span key={i}>{s}</span>
              ))}
            </p>
          )}
        </div>
        {(actions || aside) && (
          <div className="rec-head-side">
            {actions && <div className="rec-actions">{actions}</div>}
            {aside}
          </div>
        )}
      </div>
      {children}
      <div className="rec-tabs-wrap">
        <FolderTabs tabs={tabs} active={active} onSelect={onSelect} label={tabsLabel} />
      </div>
    </div>
  );
}

// —— folder tabs (customer360) ——

export interface FolderTab {
  id: string;
  label: string;
  icon: LucideIcon;
  /** Read out with the label and shown on hover: the tab's deciding figure. */
  value?: string;
}

/** One side of a tab: the flare at the base, the slanted side, the rounded top. Drawn at a fixed size so
 *  the curve never stretches with the label. */
const TAB_SIDE = 'M0 56 C8 56 12 53 14 47 L19 16 C21 6 27 0 36 0 H40 V56 Z';

function TabShape() {
  return (
    <span aria-hidden="true" className="rec-tab-shape">
      <svg viewBox="0 0 40 56" preserveAspectRatio="none" fill="currentColor">
        <path d={TAB_SIDE} />
      </svg>
      <span />
      <svg viewBox="0 0 40 56" preserveAspectRatio="none" fill="currentColor">
        <path d={TAB_SIDE} />
      </svg>
    </span>
  );
}

/** The tabs as browser tabs along the foot of the header: each overlaps the one before it, and the open one
 *  comes to the front and runs into the page below. */
export function FolderTabs({
  tabs,
  active,
  onSelect,
  label,
}: {
  tabs: FolderTab[];
  active: string;
  onSelect: (id: string) => void;
  label: string;
}) {
  return (
    <div role="tablist" aria-label={label} className="rec-tabs">
      {tabs.map((t) => {
        const on = t.id === active;
        const Icon = t.icon;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={on}
            title={t.value || undefined}
            className={`rec-tab${on ? ' on' : ''}`}
            onClick={() => onSelect(t.id)}
          >
            <TabShape />
            <span className="rec-tab-icon">
              <Icon strokeWidth={on ? 2 : 1.75} aria-hidden="true" />
            </span>
            <span className="rec-tab-label">{t.label}</span>
            {t.value && <span className="sr-only">{` ${t.value}`}</span>}
          </button>
        );
      })}
    </div>
  );
}

// —— insights ——

export interface Insight {
  id: string;
  icon: LucideIcon;
  title: string;
  text: string;
  /** Where it came from, so the reader can check it. */
  source: { label: string; href?: string };
  action?: { label: string; href?: string; onClick?: () => void };
  tone?: 'bad' | 'warn' | 'info';
}

export function InsightRow({
  title,
  note,
  insights,
  empty,
}: {
  title: string;
  note: string;
  insights: Insight[];
  /** Said when there is nothing to point out, so the row never just disappears. */
  empty: string;
}) {
  return (
    <section className="rec-insights" aria-label={title}>
      <div className="rec-insights-head">
        <h2>
          <Sparkles size={18} aria-hidden="true" />
          {title}
        </h2>
        <p>{note}</p>
      </div>
      {insights.length ? (
        <ul className="rec-insight-grid">
          {insights.map((x) => (
            <li key={x.id} className={`rec-insight${x.tone ? ` ${x.tone}` : ''}`}>
              <div className="rec-insight-top">
                <span className="rec-insight-icon">
                  <x.icon size={18} aria-hidden="true" />
                </span>
                <div>
                  <h3>{x.title}</h3>
                  <p>{x.text}</p>
                </div>
              </div>
              <div className="rec-insight-foot">
                {x.source.href ? (
                  <a className="rec-source" href={x.source.href}>
                    <Database size={13} aria-hidden="true" />
                    <span>{x.source.label}</span>
                  </a>
                ) : (
                  <span className="rec-source">
                    <Database size={13} aria-hidden="true" />
                    <span>{x.source.label}</span>
                  </span>
                )}
                {x.action &&
                  (x.action.href ? (
                    <a className="rec-act" href={x.action.href}>
                      {x.action.label} <ArrowRight size={14} aria-hidden="true" />
                    </a>
                  ) : (
                    <button type="button" className="rec-act" onClick={x.action.onClick}>
                      {x.action.label} <ArrowRight size={14} aria-hidden="true" />
                    </button>
                  ))}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="rec-insights-empty">{empty}</p>
      )}
    </section>
  );
}

/**
 * One next step instead of a row of equal choices (task 103, decision 0074): the first of a ranked list
 * with one button, the rest folded under "more" for anyone who wants to choose for themselves.
 */
export function NextStep({
  steps,
  done,
  label = 'Your next step',
}: {
  steps: Insight[];
  /** Shown when there is nothing to do, with one button of its own. */
  done: Pick<Insight, 'title' | 'text' | 'action'>;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const [first, ...more] = steps;
  const x: Omit<Insight, 'source'> & { source?: Insight['source'] } = first ?? {
    ...done,
    id: 'done',
    icon: Sparkles,
  };
  return (
    <section className={`rec-step${x.tone ? ` ${x.tone}` : ''}`} aria-label={label}>
      <div className="rec-step-main">
        <span className="rec-insight-icon">
          <x.icon size={18} aria-hidden="true" />
        </span>
        <div className="rec-step-text">
          <span className="rec-step-label">{label}</span>
          <h2>{x.title}</h2>
          <p>{x.text}</p>
          {x.source &&
            (x.source.href ? (
              <a className="rec-source" href={x.source.href}>
                <Database size={13} aria-hidden="true" />
                <span>{x.source.label}</span>
              </a>
            ) : (
              <span className="rec-source">
                <Database size={13} aria-hidden="true" />
                <span>{x.source.label}</span>
              </span>
            ))}
        </div>
        {x.action &&
          (x.action.href ? (
            <a className="btn btn-primary rec-step-go" href={x.action.href}>
              {x.action.label} <ArrowRight size={15} aria-hidden="true" />
            </a>
          ) : (
            <button type="button" className="btn btn-primary rec-step-go" onClick={x.action.onClick}>
              {x.action.label} <ArrowRight size={15} aria-hidden="true" />
            </button>
          ))}
      </div>
      {more.length > 0 && (
        <>
          <button type="button" className="rec-step-more" aria-expanded={open} onClick={() => setOpen(!open)}>
            {open
              ? 'Show fewer'
              : more.length === 1
                ? '1 more thing you could do'
                : `${more.length} more things you could do`}
            <ChevronDown size={14} aria-hidden="true" />
          </button>
          {open && (
            <ul className="rec-step-list">
              {more.map((m) => (
                <li key={m.id}>
                  <span className="grow">
                    <b>{m.title}</b>
                    <span>{m.text}</span>
                  </span>
                  {m.action &&
                    (m.action.href ? (
                      <a className="rec-act" href={m.action.href}>
                        {m.action.label} <ArrowRight size={14} aria-hidden="true" />
                      </a>
                    ) : (
                      <button type="button" className="rec-act" onClick={m.action.onClick}>
                        {m.action.label} <ArrowRight size={14} aria-hidden="true" />
                      </button>
                    ))}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

// —— the open tab ——

export function RecordBody({ children }: { children: ReactNode }) {
  return (
    <div className="rec-body" role="tabpanel">
      {children}
    </div>
  );
}

export interface Figure {
  icon: LucideIcon;
  label: string;
  value: ReactNode;
  /** Beside the value, smaller: "/ 1000", "61% verified". */
  extra?: ReactNode;
}

/** The figures each audience decides on, always in the same place. */
export function FigureStrip({ figures }: { figures: Figure[] }) {
  return (
    <div className="rec-figures card">
      {figures.map((f) => (
        <div key={f.label} className="rec-figure">
          <span className="rec-figure-icon">
            <f.icon size={20} aria-hidden="true" />
          </span>
          <div>
            <small>{f.label}</small>
            <div className="rec-figure-value">
              {f.value}
              {f.extra && <em>{f.extra}</em>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/** The record on the left, its activity and next action on the right. */
export function RecordColumns({ main, side }: { main: ReactNode; side: ReactNode }) {
  return (
    <div className="rec-columns">
      <div className="rec-col">{main}</div>
      <div className="rec-col">{side}</div>
    </div>
  );
}

/** One thing to do next, with who it is for and one button. */
export function NextAction({
  title,
  detail,
  action,
}: {
  title: string;
  detail?: string;
  action?: { label: string; href?: string; onClick?: () => void };
}) {
  return (
    <section className="rec-next" aria-label="Next action">
      <div className="rec-next-head">
        <Clock3 size={17} aria-hidden="true" /> Next action
      </div>
      <div className="rec-next-row">
        <div>
          <b>{title}</b>
          {detail && <span>{detail}</span>}
        </div>
        {action &&
          (action.href ? (
            <a className="btn btn-primary" href={action.href}>
              {action.label} <ArrowRight size={15} aria-hidden="true" />
            </a>
          ) : (
            <button type="button" className="btn btn-primary" onClick={action.onClick}>
              {action.label} <ArrowRight size={15} aria-hidden="true" />
            </button>
          ))}
      </div>
    </section>
  );
}

export interface ActivityItem {
  id: string;
  icon: LucideIcon;
  when: string;
  title: string;
  text?: string;
  href?: string;
}

export function ActivityList({ items, empty }: { items: ActivityItem[]; empty: string }) {
  if (!items.length) return <p className="muted small rec-pad">{empty}</p>;
  return (
    <ol className="rec-timeline">
      {items.map((t) => (
        <li key={t.id}>
          <span className="rec-dot" aria-hidden="true" />
          <span className="rec-tl-icon" aria-hidden="true">
            <t.icon size={16} />
          </span>
          <div>
            <div className="rec-tl-when">{t.when}</div>
            <div className="rec-tl-title">{t.href ? <a href={t.href}>{t.title}</a> : t.title}</div>
            {t.text && <div className="rec-tl-text">{t.text}</div>}
          </div>
        </li>
      ))}
    </ol>
  );
}
